import React from 'react';
import styled, { css } from 'styled-components';

import KeyboardArrowUpIcon from '@/assets/svg/keyboard_arrow_up.svg';
import KeyboardArrowDownIcon from '@/assets/svg/keyboard_arrow_down.svg';
import { colors } from '@/constants/theme';
import { media } from '@/utils/mixin';
import useCollapse from '@/hooks/useCollapse';

import { SpecificationCollapseProps } from './types';

function SpecificationCollapse({
  items,
  label,
  icon,
  isOpened,
}: SpecificationCollapseProps) {
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: isOpened,
  });

  return (
    <Component isExpanded={isExpanded}>
      <Display {...getToggleProps()}>
        {typeof icon !== 'string' ? (
          <Icon className={isExpanded ? 'expanded' : ''}>{icon}</Icon>
        ) : (
          <Icon>
            <img loading="lazy" src={icon} alt={label} />
          </Icon>
        )}

        <Title>{label}</Title>
        <DisplayIcon>
          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </DisplayIcon>
      </Display>
      <Content {...getCollapseProps()}>
        <ContentContainer>
          {items.map((item, index) => (
            <React.Fragment key={index}>
              {item.section && (
                <ListItem>
                  <ItemName isBold>{item.section}</ItemName>
                </ListItem>
              )}

              {item.options.map((option, index) => (
                <ListItem key={index}>
                  <ItemName>{option.name}</ItemName>
                  {option.value && <ItemValue>{option.value}</ItemValue>}
                </ListItem>
              ))}
            </React.Fragment>
          ))}
        </ContentContainer>
      </Content>
    </Component>
  );
}

export default SpecificationCollapse;

const Display = styled.div`
  position: relative;
  display: flex;
  padding: 20px 0 20px 56px;
  border-bottom: 1px solid ${(props) => props.theme.black100_alpha30};
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;

const Icon = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  width: 32px;
  height: 32px;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const Title = styled.span`
  flex: 1;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: ${(props) => props.theme.black};

  ${media.mobile(
    css`
      font-size: 16px;
    `
  )}
`;

const DisplayIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    fill: ${(props) => props.theme.blue100} !important;
  }
`;

const Content = styled.div``;

const ContentContainer = styled.div`
  padding: 24px 56px;

  ${media.mobile(css`
    padding: 15px 8px;
  `)}
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 16px 0;
  border-bottom: 1px solid ${(props) => props.theme.black100_alpha30};
`;

const ItemName = styled.div<{ isBold?: boolean }>`
  font-size: 14px;
  color: ${(props) => props.theme.black};

  ${({ isBold }) =>
    isBold &&
    css`
      font-weight: 700;
    `}
`;

const ItemValue = styled.div`
  max-width: 50%;
  font-size: 14px;
  font-weight: 300;
  color: ${(props) => props.theme.black};
`;

const Component = styled.div<{ isExpanded: boolean }>`
  ${({ isExpanded }) =>
    isExpanded
      ? css`
          ${Title} {
            color: ${colors.main};
          }

          ${Display} {
            border-bottom-color: ${colors.main};
          }

          ${DisplayIcon} {
            svg {
              path {
                fill: ${colors.main} !important;
              }
            }
          }
        `
      : null}
`;
